/**
 * One item list plus four renderers, in three layouts, with the drill-down.
 *
 * Everything that used to be duplicated per pillar lives here: the layout
 * switch, the tray or set bar, the drill and its back
 * control, the focus registry, the polite live region, and the ONE staggered
 * entrance the whole set gets.
 *
 * Two contract changes matter more than the rest.
 *
 * `renderCard` is required and it is used at BOTH widths. The old
 * `renderMobileCard ?? renderRailCard` fallback meant twelve of eighteen
 * pillars silently rendered a 236px comparison card as a full-width standalone
 * object on a phone — a card designed to be scanned against its siblings, with
 * no siblings beside it. Removing the fallback is the single change that
 * closes the empty-plate, the missing-action and the wrong-composition
 * defects together.
 *
 * The set also owns its non-ready faces. Loading, empty and failed are
 * different answers and they wear visually different faces, so a reader never
 * has to work out which one they are looking at.
 */
"use client";
import {
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import type { Icon as TablerIcon } from "@tabler/icons-react";
import { crcStaggerDelayMs } from "./cresultcard.motion";
import { CresultcardSetServicesProvider, useCresultcardAnnouncer, useCresultcardFocusRegistry } from "./cresultcard.focus";
import {
  CRC_NUMERIC_STYLE,
  CRC_TEXT_FAINT,
} from "./cresultcard.tokens";
import {
  CresultcardLayout,
  CresultcardSetStatus,
  type CresultcardOpenControl,
} from "./cresultcard.types";
import { CresultcardCardCarrier, CresultcardCardGrid } from "./CresultcardCardGrid";
import { CresultcardDrill, CresultcardDrillBack } from "./CresultcardDrill";
import { CresultcardEmptyFace } from "./CresultcardEmptyFace";
import { CresultcardFailureFace } from "./CresultcardFailureFace";
import { CresultcardLoadingFace } from "./CresultcardLoadingFace";
import { CresultcardProvenanceRail } from "./CresultcardProvenanceRail";
import { CresultcardOpenSurface } from "./CresultcardOpenSurface";
import { CresultcardRail, CresultcardRailItem } from "./CresultcardRail";
import { CresultcardSetBar } from "./CresultcardSetBar";
import { CresultcardSetHeader } from "./CresultcardSetHeader";
import { CresultcardTray } from "./CresultcardTray";

type CresultcardStaggerStyle = CSSProperties & {
  "--crc-stagger-delay": string;
};

const DEFAULT_CARD_MIN = 300;
const SET_STACK_GAP = 12;
/** Every column the grid resolved, whatever that number turned out to be. */
const OPEN_CARD_COLUMN_SPAN = "1 / -1";
const SHOWING_PREFIX = "Showing ";
const BACK_PREFIX = "Back to ";
const RESULTS_SUFFIX = " results";
const ESCAPE_KEY = "Escape";
/**
 * The pane takes focus when it opens, which is what makes Escape reachable at
 * all: the control that opened it is inside the list the drill just replaced,
 * so leaving focus where it was leaves it on a node that no longer exists and
 * the browser drops it to `body`. `-1` because the pane is a destination, not a
 * tab stop.
 */
const PANE_TAB_INDEX = -1;
/**
 * The drill replaces the list IN PLACE, so the widget does not move — and a
 * focus call that scrolls anyway moves the page out from under the reader
 * instead. Measured at 301px of unasked-for scroll on a keyboard open.
 */
const PANE_FOCUS_OPTIONS: FocusOptions = { preventScroll: true };

/**
 * The one control an item gets, and the only way a composition can render its
 * title as that control. A set that cannot drill hands over `undefined`, which
 * is exactly what `CresultcardIdentity.openControl` already expects.
 */
export type CresultcardItemRenderer<TItem> = (
  item: TItem,
  index: number,
  control: CresultcardOpenControl | undefined,
) => ReactNode;

export interface CresultcardSetFailure {
  readonly lane: string;
  readonly reason: string;
  readonly detail?: readonly string[];
  readonly onRetry?: () => void;
}

export interface CresultcardSetEmpty {
  readonly headline: string;
  readonly reason: string;
}

interface CresultcardSetProps<TItem> {
  readonly icon: TablerIcon;
  readonly label: string;
  /** The frame the whole set is stated against. */
  readonly context?: ReactNode;
  /**
   * What bounds the whole projection: `3 of 18`, a page, a served window, a
   * denominator that belongs to one lane of several. It wraps rather than
   * truncating, because half a bound is a different bound.
   */
  readonly bounds?: ReactNode;
  /**
   * The vendor's own strings for the SET — lane names, credits, capture counts,
   * next-offsets, attribution. `CresultcardProvenance` keeps the handles inside
   * a disclosure, so these are reachable without being unavoidable.
   */
  readonly provenance?: ReactNode;
  readonly items: readonly TItem[];
  readonly keyOf: (item: TItem, index: number) => string;
  readonly titleOf: (item: TItem, index: number) => string;
  readonly layout: CresultcardLayout;
  /** Dense line inside a bounded set. */
  readonly renderRow: CresultcardItemRenderer<TItem>;
  /** Face-up card in the horizontal deck. */
  readonly renderRailCard: CresultcardItemRenderer<TItem>;
  /** Standalone object in the flow. Required, and used at BOTH widths. */
  readonly renderCard: CresultcardItemRenderer<TItem>;
  /** Omit it and the set simply has no drill-down. */
  readonly renderExpanded?: (item: TItem, index: number) => ReactNode;
  readonly status?: CresultcardSetStatus;
  readonly empty: CresultcardSetEmpty;
  readonly failure?: CresultcardSetFailure;
  readonly cardMin?: number;
  /** For a rail whose cards are ALL short. No default, ever. */
  readonly minCardHeight?: number;
  /**
   * A persistent dock at the foot of the set, inside the tray and below the
   * list. It survives the drill, which is the whole point of a dock.
   */
  readonly foot?: ReactNode;
  readonly width?: number | string;
  /**
   * The item whose drill is open when the set mounts — the `full` face of an
   * attached result (results-as-resources DECISIONS §7.2): the detail pane
   * shown at once, the row it opened from one back-press away. Omitted, the
   * set mounts closed, as every set did before. A set with no `renderExpanded`
   * ignores it, because it has nothing to open.
   */
  readonly initialOpenIndex?: number;
}

export function CresultcardSet<TItem>(
  props: CresultcardSetProps<TItem>,
): ReactElement {
  return (
    <CresultcardSetServicesProvider>
      <CresultcardSetBody {...props} />
    </CresultcardSetServicesProvider>
  );
}

function CresultcardSetBody<TItem>({
  icon,
  label,
  context,
  bounds,
  provenance,
  items,
  keyOf,
  titleOf,
  layout,
  renderRow,
  renderRailCard,
  renderCard,
  renderExpanded,
  status = CresultcardSetStatus.Ready,
  empty,
  failure,
  cardMin = DEFAULT_CARD_MIN,
  minCardHeight,
  foot,
  width,
  initialOpenIndex,
}: CresultcardSetProps<TItem>): ReactElement {
  const [openIndex, setOpenIndex] = useState<number | null>(
    initialOpenIndex === undefined || items[initialOpenIndex] === undefined
      ? null
      : initialOpenIndex,
  );
  const restoreKeyRef = useRef<string | null>(null);
  const paneRef = useRef<HTMLDivElement | null>(null);
  const paneId = useId();
  const { announce } = useCresultcardAnnouncer();
  const { focus } = useCresultcardFocusRegistry();
  const canDrill = renderExpanded != null;

  const open = useCallback(
    (index: number): void => {
      if (!canDrill) {
        return;
      }
      setOpenIndex(index);
      announce(`${SHOWING_PREFIX}${titleOf(items[index], index)}`);
    },
    [announce, canDrill, items, titleOf],
  );

  const close = useCallback((): void => {
    setOpenIndex(null);
    announce(
      `${BACK_PREFIX}${label}, ${String(items.length)}${RESULTS_SUFFIX}`,
    );
  }, [announce, items, label]);

  // The key to come back to is recorded when the drill opens and spent when it
  // closes. Both halves live in the effect so nothing reads the ref during a
  // render, and the key survives a re-keyed list because it IS the key.
  useLayoutEffect(() => {
    if (openIndex !== null) {
      restoreKeyRef.current = keyOf(items[openIndex], openIndex);
      return;
    }
    const key = restoreKeyRef.current;
    if (key == null) {
      return;
    }
    restoreKeyRef.current = null;
    focus(key);
  }, [focus, items, keyOf, openIndex]);

  // Focus follows the drill INTO the pane. The control that opened it lives in
  // the list the pane replaced, so without this the browser drops focus to
  // `body`, Escape has nothing to travel up from, and the reader is stranded.
  useLayoutEffect((): void => {
    if (openIndex === null) {
      return;
    }
    paneRef.current?.focus(PANE_FOCUS_OPTIONS);
  }, [openIndex]);

  // Escape anywhere inside the open pane closes it and returns focus to the
  // title that opened it. A modal inside the pane — the lightbox — answers
  // Escape first and marks the event handled, so one press never closes two
  // things at once.
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>): void => {
      if (event.defaultPrevented) {
        return;
      }
      if (event.key === ESCAPE_KEY && openIndex !== null) {
        close();
      }
    },
    [close, openIndex],
  );

  /**
   * The item's own control, built HERE because the set is what owns the pane
   * and the open state. Without it a composition has no way to make its title
   * the tab stop, which is how the whole item list ended up keyboard-unreachable.
   */
  const controlFor = useCallback(
    (index: number): CresultcardOpenControl | undefined => {
      if (!canDrill) {
        return undefined;
      }
      return {
        expanded: openIndex === index,
        paneId,
        onOpen: (): void => {
          open(index);
        },
      };
    },
    [canDrill, open, openIndex, paneId],
  );

  const openItem = openIndex === null ? null : items[openIndex];
  const isOpen = openItem != null && renderExpanded != null;
  const resolvedStatus =
    status === CresultcardSetStatus.Ready && items.length === 0
      ? CresultcardSetStatus.Empty
      : status;

  const face = renderFace({
    empty,
    failure,
    layout,
    status: resolvedStatus,
  });
  /**
   * What the header is allowed to state.
   *
   * It describes a delivered answer, and a face means there is not one. A
   * count is derived from `items.length` alone, so it went on reading 3 beside
   * `No listings here` and 5 beside a loading skeleton. A face and a figure
   * about the same lane are one surface contradicting itself, so the figures
   * stand down for every face and the face is the whole answer.
   */
  const isReady = resolvedStatus === CresultcardSetStatus.Ready;

  const count = (
    <span
      style={{
        ...CRC_NUMERIC_STYLE,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.08em",
        color: CRC_TEXT_FAINT,
      }}
    >
      {items.length}
    </span>
  );

  const openSurface = (
    item: TItem,
    index: number,
    composition: ReactNode,
    fill: boolean,
  ): ReactNode => (
    <CresultcardOpenSurface
      fill={fill}
      itemKey={keyOf(item, index)}
      onOpen={() => open(index)}
    >
      {composition}
    </CresultcardOpenSurface>
  );

  const staggerStyle = (index: number): CresultcardStaggerStyle => ({
    "--crc-stagger-delay": `${String(crcStaggerDelayMs({ index }))}ms`,
  });

  /**
   * An open card takes the whole grid row.
   *
   * A drill pane is several times the height of the card it replaced, and a grid
   * row is as tall as its tallest cell: a 715px pane beside a 186px sibling left
   * 598px of empty column, with the next row starting below all of it.
   * `align-items: start` is what makes that void visible rather than a stretched
   * plate, and it stays — the fix is that the pane stops sharing a row. Spanning
   * is also the honest reading of the state: a card that is open is not a peer of
   * the cards beside it any more. Below the grid's second column it is a no-op.
   */
  const cellStyle = (index: number, cardOpen: boolean): CresultcardStaggerStyle => ({
    ...staggerStyle(index),
    gridColumn: cardOpen ? OPEN_CARD_COLUMN_SPAN : undefined,
  });

  /* ── Cards: each item is its own object, so the drill happens per card ── */
  if (layout === CresultcardLayout.Cards) {
    return (
      <div
        className="cresultcard"
        data-el="Cresultcard/SetBody/Root"
        onKeyDown={handleKeyDown}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: SET_STACK_GAP,
          minWidth: 0,
        }}
      >
        <CresultcardSetBar
          bounds={isReady ? bounds : undefined}
          context={context}
          count={isReady ? items.length : undefined}
          icon={icon}
          label={label}
        />
        {face ?? (
          <CresultcardCardGrid
            cardMin={cardMin}
          >
            {items.map((item, index) => {
              const key = keyOf(item, index);
              const cardOpen = canDrill && openIndex === index;
              return (
                <div
                  className="cresultcard__enter"
                  data-el="Cresultcard/SetBody/Entry"
                  key={key}
                  style={cellStyle(index, cardOpen)}
                >
                  <CresultcardCardCarrier
                  >
                    <CresultcardTray>
                      {cardOpen && (
                        <CresultcardSetHeader
                          context={titleOf(item, index)}
                          icon={icon}
                          label={label}
                          leading={
                            <CresultcardDrillBack
                              label={label}
                              onBack={close}
                              paneId={paneId}
                            />
                          }
                        />
                      )}
                      <CresultcardDrill
                        expanded={
                          cardOpen && renderExpanded != null ? (
                            <div
                              aria-label={titleOf(item, index)}
                              id={paneId}
                              ref={paneRef}
                              role="group"
                              tabIndex={PANE_TAB_INDEX}
                            >
                              {renderExpanded(item, index)}
                            </div>
                          ) : null
                        }
                        expandedKey={cardOpen ? key : null}
                        list={
                          <CresultcardOpenSurface
                            itemKey={key}
                            onOpen={() => open(index)}
                          >
                            {renderCard(item, index, controlFor(index))}
                          </CresultcardOpenSurface>
                        }
                      />
                    </CresultcardTray>
                  </CresultcardCardCarrier>
                </div>
              );
            })}
          </CresultcardCardGrid>
        )}
        {foot != null && (
          <div
            style={{ minWidth: 0 }}
          >
            {foot}
          </div>
        )}
        {provenance != null && isReady && (
          <CresultcardProvenanceRail
          >
            {provenance}
          </CresultcardProvenanceRail>
        )}
      </div>
    );
  }

  const body =
    layout === CresultcardLayout.Rows ? (
      <div
        data-el="Cresultcard/SetBody/List"
        style={{ position: "relative", zIndex: 1, minWidth: 0 }}
      >
        {items.map((item, index) => (
          <div
            className="cresultcard__enter"
            data-el="Cresultcard/SetBody/Entry"
            key={keyOf(item, index)}
            style={staggerStyle(index)}
          >
            {openSurface(
              item,
              index,
              renderRow(item, index, controlFor(index)),
              false,
            )}
          </div>
        ))}
      </div>
    ) : (
      <CresultcardRail
        label={`${label}, ${String(items.length)}${RESULTS_SUFFIX}`}
        minCardHeight={minCardHeight}
      >
        {items.map((item, index) => (
          <CresultcardRailItem
            key={keyOf(item, index)}
          >
            <div
              className="cresultcard__enter"
              data-el="Cresultcard/SetBody/Entry"
              style={{ ...staggerStyle(index), display: "flex", width: "100%" }}
            >
              {openSurface(
                item,
                index,
                renderRailCard(item, index, controlFor(index)),
                true,
              )}
            </div>
          </CresultcardRailItem>
        ))}
      </CresultcardRail>
    );

  return (
    <div
      data-el="Cresultcard/SetBody/Root"
      onKeyDown={handleKeyDown}
      style={{ minWidth: 0 }}
    >
      <CresultcardTray texture width={width ?? "100%"}>
        {isOpen ? (
          <CresultcardSetHeader
            context={titleOf(openItem, openIndex ?? 0)}
            icon={icon}
            label={label}
            leading={
              <CresultcardDrillBack
                label={label}
                onBack={close}
                paneId={paneId}
              />
            }
          />
        ) : (
          <CresultcardSetHeader
            bounds={isReady ? bounds : undefined}
            context={context}
            icon={icon}
            label={label}
            trailing={isReady ? count : undefined}
          />
        )}
        <CresultcardDrill
          expanded={
            isOpen && renderExpanded != null ? (
              <div
                aria-label={titleOf(openItem, openIndex ?? 0)}
                id={paneId}
                ref={paneRef}
                role="group"
                style={{ position: "relative", zIndex: 1 }}
                tabIndex={PANE_TAB_INDEX}
              >
                {renderExpanded(openItem, openIndex ?? 0)}
              </div>
            ) : null
          }
          expandedKey={
            isOpen && openIndex !== null ? keyOf(openItem, openIndex) : null
          }
          list={face ?? body}
        />
        {foot != null && (
          <div
            style={{ position: "relative", zIndex: 1, minWidth: 0 }}
          >
            {foot}
          </div>
        )}
        {provenance != null && isReady && !isOpen && (
          <CresultcardProvenanceRail>
            {provenance}
          </CresultcardProvenanceRail>
        )}
      </CresultcardTray>
    </div>
  );
}

interface RenderFaceInput {
  readonly status: CresultcardSetStatus;
  readonly layout: CresultcardLayout;
  readonly empty: CresultcardSetEmpty;
  readonly failure?: CresultcardSetFailure;
}

/** The three non-ready faces, each visually distinct from the other two. */
function renderFace({
  status,
  layout,
  empty,
  failure,
}: RenderFaceInput): ReactNode {
  if (status === CresultcardSetStatus.Loading) {
    return (
      <CresultcardLoadingFace
        layout={layout}
      />
    );
  }
  if (status === CresultcardSetStatus.Failed && failure != null) {
    return (
      <CresultcardFailureFace
        detail={failure.detail}
        lane={failure.lane}
        onRetry={failure.onRetry}
        reason={failure.reason}
      />
    );
  }
  if (status === CresultcardSetStatus.Empty) {
    return (
      <CresultcardEmptyFace
        headline={empty.headline}
        reason={empty.reason}
      />
    );
  }
  return null;
}
